import type {
  CategoryCreateInput,
  CategoryUpdateInput,
} from "@/modules/categories/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { catalogApi } from "./client";

export const catalogKeys = {
  all: ["catalog"] as const,
  categories: {
    all: ["catalog", "categories"] as const,
    list: (q: Partial<OffsetPageQueryDTO>) =>
      ["catalog", "categories", "list", q] as const,
    detail: (id: string) => ["catalog", "categories", "detail", id] as const,
  },
};

export function useCategoriesQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: catalogKeys.categories.list(q),
    queryFn: () => catalogApi.categories.list(q),
  });
}

export function useCategoryQuery(id: string) {
  return useQuery({
    queryKey: catalogKeys.categories.detail(id),
    queryFn: () => catalogApi.categories.get(id),
    enabled: !!id,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CategoryCreateInput) => catalogApi.categories.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.categories.all });
      toast.success("ສ້າງຫມວດມູ່ສຳເລັດ"); // สร้างหมวดหมู่สำเร็จ
    },
  });
}

export function useUpdateCategory(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CategoryUpdateInput) =>
      catalogApi.categories.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.categories.detail(id) });
      qc.invalidateQueries({ queryKey: catalogKeys.categories.all });
      toast.success("ແກ້ໄຂຫມວດມູ່ສຳເລັດ"); // แก้ไขหมวดหมู่สำเร็จ
    },
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => catalogApi.categories.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogKeys.categories.all });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}
